/**
 * Kazakh UI copy. Shape is checked against `Dictionary` (see ru.ts).
 *
 * Terminology follows oil-and-gas and remote-sensing usage rather than a
 * word-for-word pass: "шлейф" for the plume, "шығын" for the flow rate,
 * "жерсерік" for the satellite, "көз" for the emission source.
 *
 * Kazakh does not inflect a noun after a numeral — "5 эпизод", not
 * "5 эпизодтар" — so the plural entries below give the same form for both
 * categories on purpose.
 */
import { forms } from "./plural";
import type { Dictionary } from "./ru";

export const kk: Dictionary = {
  app: {
    title: "KGS Methane Monitoring",
    subtitle: "Satellite-based methane detection system",
    demoBadge: "ДЕМО",
    demoTitle: "Синтетикалық демо-деректер, нақты өлшемдер емес",
  },
  header: {
    themeToggleToLight: "Ашық тақырыпқа ауысу",
    themeToggleToDark: "Қою тақырыпқа ауысу",
    languageLabel: "Интерфейс тілі",
  },
  languages: {
    ru: "Русский",
    kk: "Қазақша",
    en: "English",
  },
  stats: {
    detections: "Детекциялар",
    emissions: "кг/сағ",
    countLabel: "Есептеу:",
    modeExtent: "ағымдағы көріністе",
    modeCountry: "бүкіл аумақ бойынша",
    modeExtentHint: "Тек картаның көрінетін тіктөртбұрышындағы детекциялар есептеледі",
    modeCountryHint: "Сүзгілерді ескере отырып, бүкіл мониторинг аумағындағы детекциялар есептеледі",
  },
  basemap: {
    positron: "Карта (ашық)",
    darkMatter: "Карта (қою)",
    satellite: "Жерсерік",
    satelliteLabels: "Жерсерік + жазулар",
    osm: "OSM стандарты",
    topo: "Бедер",
  },
  map: {
    resetExtent: "Бүкіл аумақ",
    fullscreen: "Толық экран режимі",
    zoomIn: "Үлкейту",
    zoomOut: "Кішірейту",
    basemap: "Негізгі карта",
    region: "Аймақ",
    cursorPosition: "Меңзер координаттары",
    copyCoordinates: "Координаттарды көшіру",
    coordinatesCopied: "Көшірілді",
  },
  layers: {
    title: "Қабаттар",
    open: "Қабаттар панелін көрсету",
    collapse: "Жию",
    groupBasemap: "Негізгі карта",
    groupBorders: "Шекаралар",
    groupData: "Мониторинг деректері",
    country: "Ел шекаралары",
    regions: "Облыс шекаралары",
    plumes: "Шлейф полигондары",
    raster: "Растрлық концентрация",
    rasterOpacity: "Растр мөлдірлігі",
    rasterPending: "Эпизод таңдалғанда растр пайда болады",
    sources: "Нүктелік көздер",
    aoi: "Қызығушылық аймақтары",
  },
  filters: {
    title: "Сүзгілер",
    open: "Сүзгілерді көрсету",
    collapse: "Жию",
    period: "Кезең",
    presetD7: "7 күн",
    presetD30: "30 күн",
    presetD90: "90 күн",
    presetY1: "жыл",
    presetAll: "бүкіл кезең",
    satellites: "Жерсеріктер",
    minRate: "Ең аз шығын, кг/сағ",
    apply: "Қолдану",
    reset: "Тазалау",
    export: "Деректерді экспорттау",
    exportSoon: "Деректерді экспорттау 7-кезеңде қосылады",
    chipPeriod: "Кезең",
    chipMinRate: "Ең аз шығын",
    removeFilter: "Сүзгіні алып тастау",
    changesPlural: forms({
      one: "{n} өзгеріс",
      other: "{n} өзгеріс",
    }),
  },
  satelliteNames: {
    "sentinel-2": "Sentinel-2",
    "landsat-8": "Landsat 8",
    "landsat-9": "Landsat 9",
    emit: "EMIT",
  },
  states: {
    loading: "Деректер жүктелуде…",
    emptyTitle: "Детекция табылмады",
    emptyBody:
      "Таңдалған сүзгілер бойынша ештеңе табылмады. Кезеңді кеңейтіңіз немесе ең аз шығынды азайтыңыз.",
    emptyReset: "Сүзгілерді тазалау",
    errorTitle: "Бірдеңе дұрыс болмады",
    errorBody: "Мониторинг деректерін жүктеу мүмкін болмады.",
    errorRetry: "Бетті жаңарту",
  },
  export: {
    title: "Деректерді экспорттау",
    format: "Формат",
    area: "Аумақ",
    areaExtent: "Ағымдағы экстент",
    areaCountry: "Бүкіл аумақ",
    fields: "Өрістер жиыны",
    fieldsAll: "Барлық өрістер",
    fieldsBasic: "Тек негізгілері",
    submit: "Жүктеп алу",
    cancel: "Болдырмау",
    close: "Жабу",
    exporting: "Дайындалуда…",
    empty: "Ағымдағы сүзгілер бойынша экспорттайтын дерек жоқ",
    done: "Жүктелген жолдар:",
    error: "Экспорттау мүмкін болмады (XLSX үшін npm install орындаңыз)",
  },
  detail: {
    episode: "Эпизод",
    close: "Жабу",
    durationDays: "күн",
    detections: "детекция",
    obsDates: "түсірілім күні",
    maxRate: "макс. кг/сағ",
    silenceCaveat:
      "Күндер арасында детекцияның болмауы шығарынды тоқтады дегенді білдірмейді: жерсерік сол күндері көзді бақыламаған немесе сигнал басылған болуы мүмкін.",
    attributes: "Атрибуттар",
    sensors: "Сенсорлар",
    extent: "Шлейф ұзындығы",
    totalMass: "Жалпы масса",
    notCalculated: "есептелмейді",
    coordinates: "Көз координаттары",
    source: "Көз",
    sector: "Сектор",
    operator: "Оператор",
    region: "Облыс",
    noSource: "байланыстырылмаған",
    chartTitle: "Шығын динамикасы",
    chartEmpty: "График үшін сандық бағалау жоқ",
    rasterTitle: "Растрлық алдын ала қарау",
    rasterEmpty: "Растрлық алдын ала қарау қолжетімсіз",
    tableTitle: "Детекциялар",
    colDate: "Күні",
    colRate: "Шығын",
    colConfidence: "Сенім.",
    colSatellite: "Жерсерік",
    download: "Эпизод деректерін жүктеп алу",
    downloadSoon: "Эпизодты экспорттау 7-кезеңде қосылады",
    km: "км",
    kg: "кг",
    kgh: "кг/сағ",
    notFound: "Эпизод табылмады",
    unquantified: "бағалау жоқ",
    noWind: "жел жоқ",
    failed: "қате",
    meanRate: "Орташа шығын",
    gapDays: "Кластерлеу терезесі",
    mergedWarn: "Ұзындығы үлкен — әртүрлі көздер біріктірілген болуы мүмкін.",
    sourceEpisodes: "Көз эпизодтары",
    noEpisodes: "Тіркелген эпизодтар жоқ",
  },
  sectorNames: {
    oil_gas: "Мұнай және газ",
    coal_mine: "Көмір",
    coal: "Көмір",
    landfill: "Қалдықтар",
    waste: "Қалдықтар",
    agriculture: "Ауыл шаруашылығы",
    other: "Басқа",
  },
  timeline: {
    play: "Ойнату",
    pause: "Кідірту",
    speed: "Жылдамдық",
    cursor: "Ағымдағы күн",
    window: "Үзіліс",
    windowUnit: "күн",
    windowHint:
      "Ағымдағы күннен қанша күн бұрынғы детекциялар әлі өзекті болып саналады. Мөлдірлік детекцияның жасын (уақытын) көрсетеді, шығарынды күшін емес.",
    noDates: "Таңдалған кезеңде детекция жоқ",
    activeEpisodes: forms({
      one: "{n} белсенді эпизод",
      other: "{n} белсенді эпизод",
    }),
  },
  legend: {
    title: "Шығын градациясы",
    open: "Аңызды көрсету",
    collapse: "Аңызды жию",
    low: "500 кг/сағ-тан аз",
    moderate: "500 – 1 000 кг/сағ",
    high: "1 000 – 2 000 кг/сағ",
    severe: "2 000 кг/сағ-тан көп",
    unknown: "сандық бағалау жоқ",
    filterHint: "Жолды басыңыз — тек осы санатты көрсету",
    showAll: "Барлығын көрсету",
  },
};
